// ── Resource upload section — the 503 gate-closed honest state (Wave 8) ─────
// CONTRACT (blueprint §1, trap #4; verified against
// apps/vjal-coaching-api/src/routes/coaching-shell-resource-routes.ts): the
// resource upload operations — complete / download / delete — ALL return a
// HARDCODED HTTP 503 (R2 file storage is not wired yet). This is NOT a failure:
// it is a GATE-CLOSED honest state ("file storage not available yet"). The
// affordances are DISABLED and call NOTHING — they never fake an upload, a
// download, or a delete. When R2 lands and the 503 lifts, this is where the real
// upload/download/delete wire in; until then it is an honest disabled placeholder.
//
// Only STAFF (manage.resources) ever see the manage-side upload/delete controls;
// the download affordance follows the per-role / per-category download rule
// (reference videos are view-only for coaches) and is owned by the download
// section. This section is the file-storage gate posture for the manage roles.

import { Alert, Button, Module, ModuleBody, ModuleHead } from "@vjal/ui";
import { Trash2, UploadCloud } from "lucide-react";

import type { CoachingRole } from "../../../domain/roles";
import { canManageResources } from "../resources-helpers";

export function ResourceUploadSection({ role }: { role: CoachingRole }) {
  // Manage-side upload/delete are STAFF-only. A non-staff role never sees them.
  if (!canManageResources(role)) return null;

  return (
    <Module span={4}>
      <ModuleHead title="File storage" subtitle="Uploads and file management for this resource." />
      <ModuleBody className="space-y-3" data-testid="resource-upload">
        <div data-honest-posture="gate-closed" className="space-y-3">
          <Alert variant="warning">
            File storage isn't available yet — uploads, file downloads, and file deletion are being connected. Nothing was
            changed. The resource's link is still available above.
          </Alert>
          {/* Disabled by design: the upload/delete endpoints return 503 (R2
              closed), so these controls fire NOTHING. They never fake a transfer. */}
          <Button type="button" variant="secondary" leftIcon={<UploadCloud size={16} />} disabled aria-label="Upload file">
            Upload not available yet
          </Button>
          <Button type="button" variant="secondary" leftIcon={<Trash2 size={16} />} disabled aria-label="Delete file">
            Delete file not available yet
          </Button>
        </div>
      </ModuleBody>
    </Module>
  );
}
